/**
 * A small, safe Markdown reader (TMMAPP-0142 §E 24): model answers (and, later, help articles) are
 * parsed into a tree the app renders as React elements — never HTML. Raw HTML in the source stays
 * literal text, a link keeps only an `http(s):` or `mailto:` destination, and an image is read as
 * its alt text (no images from the model). An image keeps its raw destination (`src`), and a link to
 * a bare article id (`[Your first import](your-first-import)`) is an `article` node, for the help
 * library's renderer (TMMAPP-0142 U2); every other renderer shows both as text.
 *
 * Blocks: paragraphs, ATX headings, fenced code, `-` / `*` / `+` and numbered lists, block quotes,
 * GitHub-style tables and thematic breaks. Inline: code spans, `**strong**` / `__strong__`,
 * `*emphasis*` / `_emphasis_`, `[text](url)` links, `![alt](url)` images and hard breaks.
 *
 * Bounded work (TMMAPP-0142 H8): at most `MARKDOWN_MAX_LENGTH` characters are read as Markdown (the
 * rest stays plain text), nesting stops at `MARKDOWN_MAX_DEPTH` (deeper text is kept as typed), and
 * brackets are matched once per text rather than rescanned per opener, so unclosed brackets,
 * emphasis markers or backticks can't make the reader quadratic.
 */

/** The longest source read as Markdown; anything after it is a plain-text paragraph. */
export const MARKDOWN_MAX_LENGTH = 20_000;
/** The deepest nesting of quotes, lists, links and emphasis; deeper text is kept as typed. */
export const MARKDOWN_MAX_DEPTH = 8;

export type MdInline =
  | { type: 'text'; text: string }
  | { type: 'code'; text: string }
  | { type: 'strong'; children: MdInline[] }
  | { type: 'em'; children: MdInline[] }
  | { type: 'link'; href: string; children: MdInline[] }
  | { type: 'image'; alt: string; src: string }
  | { type: 'article'; id: string; children: MdInline[] }
  | { type: 'break' };

export type MdBlock =
  | { type: 'paragraph'; children: MdInline[] }
  | { type: 'heading'; level: 1 | 2 | 3 | 4 | 5 | 6; children: MdInline[] }
  | { type: 'code'; text: string }
  | { type: 'list'; ordered: boolean; start: number; items: MdBlock[][] }
  | { type: 'quote'; children: MdBlock[] }
  | { type: 'table'; header: MdInline[][]; rows: MdInline[][][] }
  | { type: 'rule' };

const SAFE_HREF = /^(https?:\/\/|mailto:)/i;
/** A help article's id (`your-first-import`): lowercase words joined by hyphens. */
const ARTICLE_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** The destination when it is safe to follow, else null (the text stays, unlinked). */
export function safeHref(raw: string): string | null {
  const href = raw.trim();
  if (!SAFE_HREF.test(href)) return null;
  // No whitespace or control characters inside a destination.
  for (const char of href) {
    const code = char.charCodeAt(0);
    if (code <= 0x20 || code === 0x7f) return null;
  }
  return href;
}

/**
 * Each opener's matching closer (-1 when unclosed), in one pass: the same answer as scanning
 * forward from the opener with a depth count, without rescanning the text for every opener.
 */
function matchPairs(text: string, open: string, close: string): Int32Array {
  const match = new Int32Array(text.length).fill(-1);
  const stack: number[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (char === '\\') {
      i += 1;
    } else if (char === open) {
      stack.push(i);
    } else if (char === close) {
      const opener = stack.pop();
      if (opener !== undefined) match[opener] = i;
    }
  }
  return match;
}

const ESCAPABLE = new Set('\\`*_{}[]()#+-.!|>~<'.split(''));

/** A link's label never holds another link: an inner link becomes its own text (no nested <a>). */
function flattenLinks(nodes: MdInline[]): MdInline[] {
  return nodes.flatMap((node): MdInline[] => {
    if (node.type === 'link' || node.type === 'article') return flattenLinks(node.children);
    if (node.type === 'strong' || node.type === 'em') {
      return [{ ...node, children: flattenLinks(node.children) }];
    }
    return [node];
  });
}

/** Inline Markdown into nodes. */
export function parseInline(text: string, depth = 0): MdInline[] {
  if (depth >= MARKDOWN_MAX_DEPTH) return text.length > 0 ? [{ type: 'text', text }] : [];
  const out: MdInline[] = [];
  const brackets = matchPairs(text, '[', ']');
  const parens = matchPairs(text, '(', ')');
  // A marker with no closer from some point on has none after it either: later openers skip the scan.
  const unclosedFrom = new Map<string, number>();
  const closes = (marker: string, from: number) => {
    const known = unclosedFrom.get(marker);
    return known === undefined || from < known;
  };
  const unclosed = (marker: string, from: number) => {
    unclosedFrom.set(marker, Math.min(unclosedFrom.get(marker) ?? from, from));
  };
  let buffer = '';
  const flush = () => {
    if (buffer.length > 0) out.push({ type: 'text', text: buffer });
    buffer = '';
  };
  let i = 0;
  while (i < text.length) {
    const char = text[i]!;
    if (char === '\\' && i + 1 < text.length && ESCAPABLE.has(text[i + 1]!)) {
      buffer += text[i + 1];
      i += 2;
      continue;
    }
    if (char === '\n') {
      // Two trailing spaces or a backslash before the newline is a hard break; else a space.
      if (buffer.endsWith('  ')) {
        buffer = buffer.replace(/ +$/, '');
        flush();
        out.push({ type: 'break' });
      } else {
        buffer = buffer.replace(/ +$/, '');
        buffer += ' ';
      }
      i += 1;
      continue;
    }
    if (char === '`') {
      let run = i;
      while (text[run] === '`') run += 1;
      const ticks = text.slice(i, run);
      const end = closes(ticks, run) ? text.indexOf(ticks, run) : -1;
      if (end !== -1) {
        flush();
        out.push({ type: 'code', text: text.slice(i + ticks.length, end).trim() });
        i = end + ticks.length;
        continue;
      }
      unclosed(ticks, run);
      buffer += ticks;
      i += ticks.length;
      continue;
    }
    if (char === '!' && text[i + 1] === '[') {
      const close = brackets[i + 1] ?? -1;
      if (close !== -1 && text[close + 1] === '(') {
        const end = parens[close + 1] ?? -1;
        if (end !== -1) {
          flush();
          const src =
            text
              .slice(close + 2, end)
              .trim()
              .split(/\s+/)[0] ?? '';
          out.push({ type: 'image', alt: text.slice(i + 2, close), src });
          i = end + 1;
          continue;
        }
      }
    }
    if (char === '[') {
      const close = brackets[i] ?? -1;
      if (close !== -1 && text[close + 1] === '(') {
        const end = parens[close + 1] ?? -1;
        if (end !== -1) {
          const label = text.slice(i + 1, close);
          const destination =
            text
              .slice(close + 2, end)
              .trim()
              .split(/\s+/)[0] ?? '';
          const href = safeHref(destination);
          flush();
          const children = parseInline(label, depth + 1);
          if (href !== null) {
            out.push({ type: 'link', href, children: flattenLinks(children) });
          } else if (ARTICLE_ID.test(destination)) {
            out.push({ type: 'article', id: destination, children: flattenLinks(children) });
          } else {
            out.push(...children);
          }
          i = end + 1;
          continue;
        }
      }
    }
    if (char === '*' || char === '_') {
      const double = text[i + 1] === char;
      const marker = double ? char + char : char;
      const start = i + marker.length;
      // An opener is followed by a non-space; `_` inside a word (snake_case) is not emphasis.
      const prev = i > 0 ? text[i - 1]! : ' ';
      const intraword = char === '_' && /[\p{L}\p{N}]/u.test(prev);
      if (!intraword && start < text.length && !/\s/.test(text[start]!) && closes(marker, start)) {
        let end = text.indexOf(marker, start);
        while (end !== -1 && (/\s/.test(text[end - 1]!) || (!double && text[end + 1] === char))) {
          end = text.indexOf(marker, end + marker.length);
        }
        if (end !== -1 && end > start) {
          flush();
          const children = parseInline(text.slice(start, end), depth + 1);
          out.push(double ? { type: 'strong', children } : { type: 'em', children });
          i = end + marker.length;
          continue;
        }
        if (end === -1) unclosed(marker, start);
      }
    }
    buffer += char;
    i += 1;
  }
  flush();
  return out;
}

const FENCE = /^ {0,3}(`{3,}|~{3,})/;
const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?[ \t]*#*[ \t]*$/;
const RULE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const BULLET = /^( {0,3})([-*+])[ \t]+(.*)$/;
const ORDERED = /^( {0,3})(\d{1,9})[.)][ \t]+(.*)$/;
const QUOTE = /^ {0,3}>[ \t]?(.*)$/;
const TABLE_SEPARATOR = /^ {0,3}\|?[ \t]*:?-+:?[ \t]*(\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/;

function splitRow(line: string): string[] {
  let row = line.trim();
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|') && !row.endsWith('\\|')) row = row.slice(0, -1);
  const cells: string[] = [];
  let cell = '';
  for (let i = 0; i < row.length; i += 1) {
    if (row[i] === '\\' && row[i + 1] === '|') {
      cell += '|';
      i += 1;
    } else if (row[i] === '|') {
      cells.push(cell.trim());
      cell = '';
    } else {
      cell += row[i];
    }
  }
  cells.push(cell.trim());
  return cells;
}

function isBlockStart(line: string): boolean {
  return (
    FENCE.test(line) ||
    HEADING.test(line) ||
    RULE.test(line) ||
    BULLET.test(line) ||
    ORDERED.test(line) ||
    QUOTE.test(line)
  );
}

/** Block Markdown into a tree; past `MARKDOWN_MAX_LENGTH` the source is a plain-text paragraph. */
export function parseMarkdown(source: string): MdBlock[] {
  const normalized = source.replace(/\r\n?/g, '\n');
  if (normalized.length <= MARKDOWN_MAX_LENGTH) return parseLines(normalized.split('\n'), 0);
  // Cut at a line end near the limit, so the Markdown part ends on a whole line.
  const lineEnd = normalized.lastIndexOf('\n', MARKDOWN_MAX_LENGTH);
  const cut = lineEnd > MARKDOWN_MAX_LENGTH / 2 ? lineEnd : MARKDOWN_MAX_LENGTH;
  const blocks = parseLines(normalized.slice(0, cut).split('\n'), 0);
  const rest = normalized.slice(cut).trim();
  if (rest.length > 0) blocks.push({ type: 'paragraph', children: [{ type: 'text', text: rest }] });
  return blocks;
}

function parseLines(lines: string[], depth: number): MdBlock[] {
  if (depth >= MARKDOWN_MAX_DEPTH) {
    const text = lines.join('\n').trim();
    return text.length > 0 ? [{ type: 'paragraph', children: [{ type: 'text', text }] }] : [];
  }
  const blocks: MdBlock[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === '') {
      i += 1;
      continue;
    }
    const fence = FENCE.exec(line);
    if (fence !== null) {
      const marker = fence[1]!;
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i]!.trimStart().startsWith(marker)) {
        body.push(lines[i]!);
        i += 1;
      }
      i += 1;
      blocks.push({ type: 'code', text: body.join('\n') });
      continue;
    }
    const heading = HEADING.exec(line);
    if (heading !== null) {
      const level = heading[1]!.length as 1 | 2 | 3 | 4 | 5 | 6;
      blocks.push({ type: 'heading', level, children: parseInline(heading[2] ?? '', depth) });
      i += 1;
      continue;
    }
    if (RULE.test(line)) {
      blocks.push({ type: 'rule' });
      i += 1;
      continue;
    }
    if (QUOTE.test(line)) {
      const body: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i]!)) {
        body.push(QUOTE.exec(lines[i]!)![1]!);
        i += 1;
      }
      blocks.push({ type: 'quote', children: parseLines(body, depth + 1) });
      continue;
    }
    const bullet = BULLET.exec(line);
    const ordered = bullet === null ? ORDERED.exec(line) : null;
    if (bullet !== null || ordered !== null) {
      const isOrdered = ordered !== null;
      const pattern = isOrdered ? ORDERED : BULLET;
      const start = isOrdered ? Number(ordered[2]) : 1;
      const indent = (bullet ?? ordered)![1]!.length;
      const items: string[][] = [];
      while (i < lines.length) {
        const current = lines[i]!;
        const match = pattern.exec(current);
        if (match !== null && match[1]!.length <= indent + 1) {
          items.push([match[3]!]);
          i += 1;
          continue;
        }
        if (current.trim() === '') {
          // A blank line ends the list unless the next line continues it (indented or a sibling).
          const next = lines[i + 1];
          if (next !== undefined && (/^\s{2,}\S/.test(next) || pattern.test(next))) {
            items.at(-1)?.push('');
            i += 1;
            continue;
          }
          break;
        }
        if (/^\s{2,}\S/.test(current) && items.length > 0) {
          items.at(-1)!.push(current.replace(/^\s{2,4}/, ''));
          i += 1;
          continue;
        }
        if (items.length > 0 && !isBlockStart(current)) {
          // A lazy continuation of the item's paragraph.
          items.at(-1)!.push(current.trim());
          i += 1;
          continue;
        }
        break;
      }
      blocks.push({
        type: 'list',
        ordered: isOrdered,
        start,
        items: items.map((item) => parseLines(item, depth + 1)),
      });
      continue;
    }
    if (line.includes('|') && i + 1 < lines.length && TABLE_SEPARATOR.test(lines[i + 1]!)) {
      const header = splitRow(line);
      const width = header.length;
      i += 2;
      const rows: MdInline[][][] = [];
      while (i < lines.length && lines[i]!.trim() !== '' && lines[i]!.includes('|')) {
        const cells = splitRow(lines[i]!);
        rows.push(
          Array.from({ length: width }, (_unused, index) => parseInline(cells[index] ?? '', depth)),
        );
        i += 1;
      }
      blocks.push({ type: 'table', header: header.map((cell) => parseInline(cell, depth)), rows });
      continue;
    }
    const paragraph: string[] = [];
    while (i < lines.length && lines[i]!.trim() !== '' && !isBlockStart(lines[i]!)) {
      if (
        paragraph.length > 0 &&
        lines[i]!.includes('|') &&
        i + 1 < lines.length &&
        TABLE_SEPARATOR.test(lines[i + 1]!)
      ) {
        break;
      }
      paragraph.push(lines[i]!);
      i += 1;
    }
    if (paragraph.length === 0) {
      // A line no rule takes (defensive): keep it as text.
      paragraph.push(line);
      i += 1;
    }
    blocks.push({ type: 'paragraph', children: parseInline(paragraph.join('\n').trim(), depth) });
  }
  return blocks;
}
